import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { SquareArrowOutUpRightIcon } from 'lucide-react'

import {
  AirdropEligibilityBreakdown,
  AirdropEligibilityBreakdownProps,
} from '../airdrop-eligibility-breakdown/AirdropEligibilityBreakdown'
import FollowUsOnXButtonIcon from '@/ui/assets/spk-airdrop/follow-us-on-x-button-icon.svg?react'
import { Button, ButtonIcon } from '@/ui/atoms/button/Button'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { links } from '@/ui/constants/links'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface EligibilityCheckResultPanelProps extends AirdropEligibilityBreakdownProps {
  address: CheckedAddress
  totalAllocation: NormalizedNumber
  variant: 'connected-wallet' | 'manual-input'
  openConnectWalletModal: () => void
  resetAddress: () => void
}

export function EligibilityCheckResultPanel({
  address,
  spk,
  totalAllocation,
  airdrops,
  variant,
  openConnectWalletModal,
  resetAddress,
}: EligibilityCheckResultPanelProps) {
  if (totalAllocation.eq(0)) {
    return (
      <EligibilityCheckNegativePanel
        address={address}
        variant={variant}
        openConnectWalletModal={openConnectWalletModal}
        resetAddress={resetAddress}
      />
    )
  }

  return (
    <EligibilityCheckPositivePanel
      address={address}
      spk={spk}
      totalAllocation={totalAllocation}
      airdrops={airdrops}
      variant={variant}
      resetAddress={resetAddress}
    />
  )
}

interface EligibilityCheckNegativePanelProps {
  address: CheckedAddress
  variant: EligibilityCheckResultPanelProps['variant']
  openConnectWalletModal: () => void
  resetAddress: () => void
}

function EligibilityCheckNegativePanel({ address, variant, resetAddress }: EligibilityCheckNegativePanelProps) {
  return (
    <Panel spacing="l" className="flex flex-col gap-5 rounded-md sm:gap-8">
      <div className="flex flex-col gap-4">
        <div className="typography-heading-5 text-primary">
          {variant === 'connected-wallet'
            ? "You weren't eligible for the Ignition, pre-farming, or Layer3 airdrop."
            : `${CheckedAddress.formatShort(address)} was not eligible for the Ignition, pre-farming, or Layer3 airdrop.`}
        </div>
        <div className="typography-body-4 text-secondary">
          This wallet address either did not meet the airdrop eligibility criteria or has been flagged as restricted due
          to compliance requirements.
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <div className="typography-label-3 text-secondary">EVM Address</div>
          <div className="flex items-center justify-between rounded-sm bg-secondary p-4 sm:p-6">
            <div
              className="typography-label-2 overflow-hidden text-ellipsis whitespace-nowrap text-primary"
              data-testid={testIds.spkAirdrop.eligibilityCheckResultPanel.evmAddress}
            >
              {address}
            </div>
          </div>
          {variant === 'connected-wallet' && (
            <div className="typography-body-4 text-secondary">
              Switch accounts in your wallet to try a different address.
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <div
            className={cn(
              'flex w-full flex-col items-stretch gap-2.5 sm:grid sm:w-fit sm:grid-cols-1 sm:items-center',
              variant === 'manual-input' && 'sm:grid-cols-2',
            )}
          >
            <ExternalLinkButton size="l" to={links.docs.airdrop.summary} variant="tertiary">
              Learn more
              <ButtonIcon icon={SquareArrowOutUpRightIcon} />
            </ExternalLinkButton>
            {variant === 'manual-input' && (
              <Button size="l" onClick={resetAddress}>
                Check another address
              </Button>
            )}
          </div>
        </div>
      </div>
    </Panel>
  )
}

interface EligibilityCheckPositivePanelProps extends AirdropEligibilityBreakdownProps {
  address: CheckedAddress
  totalAllocation: NormalizedNumber
  variant: EligibilityCheckResultPanelProps['variant']
  resetAddress: () => void
}

function EligibilityCheckPositivePanel({
  address,
  spk,
  totalAllocation,
  airdrops,
  variant,
  resetAddress,
}: EligibilityCheckPositivePanelProps) {
  return (
    <Panel spacing="l" className="flex flex-col gap-5 sm:gap-8">
      <div className="flex flex-col gap-4">
        <div className="typography-heading-5 text-primary">
          {variant === 'connected-wallet' ? 'You were' : `${CheckedAddress.formatShort(address)} was`} eligible for the{' '}
          <span className="whitespace-nowrap">SPK Airdrop</span>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <div className="mb-3 flex flex-col gap-3">
          <div className="typography-label-3 text-secondary">Total airdrop amount</div>
          <div className="flex items-center justify-between rounded-sm bg-secondary p-4 sm:p-6">
            <div className="flex items-center gap-2">
              <TokenIcon token={spk} className="size-5 sm:size-8" />
              <div className="flex items-baseline gap-1">
                <div
                  className="typography-label-1 sm:typography-heading-3 text-primary"
                  data-testid={testIds.spkAirdrop.eligibilityCheckResultPanel.totalAllocation}
                >
                  {spk.format(totalAllocation, { style: 'compact' })} {spk.symbol}
                </div>
              </div>
            </div>
          </div>
        </div>
        <AirdropEligibilityBreakdown airdrops={airdrops} spk={spk} />
        <div className="flex justify-end">
          <div
            className={cn(
              'flex w-full flex-col items-stretch gap-2.5 sm:grid sm:w-fit sm:grid-cols-1 sm:items-center',
              variant === 'manual-input' && 'sm:grid-cols-2',
            )}
          >
            {variant === 'manual-input' && (
              <Button variant="tertiary" size="l" onClick={resetAddress}>
                Check another address
              </Button>
            )}
            <ExternalLinkButton size="l" to={links.social.x}>
              <ButtonIcon icon={FollowUsOnXButtonIcon} />
              Follow us on X
            </ExternalLinkButton>
          </div>
        </div>
      </div>
    </Panel>
  )
}
